'use client';

/**
 * CRM application tools grouped with the shared agent-tool presentation.
 *
 * Each CRM operation uses the same full-width card as general and delivery
 * tools. Its switch stays visible while collapsed and auto-saves via the
 * dedicated hook toggle methods. The config dialog
 * is config-only (allowed fields / stages) and contains NO master
 * toggle or instruction textarea. Operator
 * instructions are edited via the shared `<ToolInstructionsDialog>`
 * accessible from a footer link in the config dialog.
 *
 * Gating:
 *
 *   - 404 `crm_binding_not_found` → both cards locked with
 *     `lockedReason`; shared "Connect CRM integration" banner inside
 *     the group body.
 *   - 409 `crm_tools_not_supported` → both cards locked with a
 *     provider-specific reason.
 *   - 200 with partial `supportedTools` → unsupported cards are locked
 *     individually while supported tools remain configurable.
 *   - 502 `crm_fetch_failed` → banner with Retry button; cards
 *     disabled.
 *   - 200 → full editing (settings come from agent config, always
 *     available alongside schema/pipelines).
 */

import {
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import {
  Database,
  Edit3,
  Move,
  AlertTriangle,
  RefreshCw,
  Settings2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AgentToolCard,
  AgentToolCategory,
} from './agent-tool-presentation';
import {
  useCrmToolsConfig,
  type CrmToolsChannel,
} from '@/hooks/agents/use-crm-tools-config';
import {
  CRM_OPPORTUNITY_TOOL_NAME,
  type CrmOpportunityToolName,
  type CrmTerminology,
} from '@/lib/ai/tools/crm-opportunity/client-types';
import { CrmToolConfigDialog } from './crm-tool-config-dialog';
import { DeliveryToolsRecommendation } from '@/components/deliveries/delivery-tools-recommendation';
import { useAgentDeliverySettingsQuery } from '@/hooks/queries/delivery-bindings';
import type { DeliveryScopeType } from '@/lib/deliveries/config';

const DEFAULT_TERMINOLOGY: CrmTerminology = {
  opportunity: 'Opportunity',
  opportunityPlural: 'Opportunities',
  pipeline: 'Pipeline',
  pipelinePlural: 'Pipelines',
  stage: 'Stage',
  stagePlural: 'Stages',
};

interface CrmToolsGroupProps {
  channel: CrmToolsChannel;
  agentId: string;
  canManage: boolean;
  /**
   * Open the shared `<ToolInstructionsDialog>` for the named CRM
   * tool. State lives at the file (tab) level — the same place
   * Shopify mounts its instruction dialogs. The group passes these
   * down to the config dialog footer link.
   */
  onOpenUpdateFieldsInstructions: () => void;
  onOpenMoveStageInstructions: () => void;
  /** Fired after every successful settings/enable save (see hook option). */
  onConfigChanged?: () => void;
  /**
   * Whether an enabled CRM binding exists for this agent. When `false` we skip
   * the `refresh-schema` probe (it would only return `no_binding` anyway) and
   * render the no-binding lock directly. Defaults to `true` (always probe) for
   * callers that don't track binding state.
   */
  hasBinding?: boolean;
}

export function CrmToolsGroup({
  channel,
  agentId,
  canManage,
  onOpenUpdateFieldsInstructions,
  onOpenMoveStageInstructions,
  onConfigChanged,
  hasBinding = true,
}: CrmToolsGroupProps) {
  const cfg = useCrmToolsConfig({ agentId, channel, autoLoad: hasBinding, onSaved: onConfigChanged });
  const deliverySettingsQuery = useAgentDeliverySettingsQuery(
    getDeliveryScopeType(channel),
    agentId,
  );
  // No enabled binding → the probe is a guaranteed `crm_binding_not_found`, so
  // surface that state without the round-trip.
  const status = hasBinding ? cfg.status : ({ kind: 'no_binding' } as const);
  const [expandedTools, setExpandedTools] = useState<Set<CrmOpportunityToolName>>(
    new Set(),
  );
  const [updateFieldsDialogOpen, setUpdateFieldsDialogOpen] = useState(false);
  const [moveStageDialogOpen, setMoveStageDialogOpen] = useState(false);

  const t =
    status.kind === 'ok'
      ? status.data.terminology
      : DEFAULT_TERMINOLOGY;
  const updateFieldsLockedReason = buildToolLockReason(
    status,
    CRM_OPPORTUNITY_TOOL_NAME.updateFields
  );
  const moveStageLockedReason = buildToolLockReason(
    status,
    CRM_OPPORTUNITY_TOOL_NAME.moveStage
  );
  const refreshHint =
    status.kind === 'ok' &&
    !status.data.supportedTools.includes(CRM_OPPORTUNITY_TOOL_NAME.updateFields) &&
    status.data.supportedTools.includes(CRM_OPPORTUNITY_TOOL_NAME.moveStage)
      ? `Refresh CRM ${t.stagePlural.toLowerCase()} before choosing allowed values.`
      : 'Refresh CRM fields and stages before choosing allowed values.';

  return (
    <>
      <AgentToolCategory
        title="CRM Application Tools"
        description={`Update the current CRM ${t.opportunity.toLowerCase()} and move it through your configured workflow.`}
        tone="crm_application"
        icon={Database}
      >
        {deliverySettingsQuery.data?.effectiveBinding ? (
          <DeliveryToolsRecommendation />
        ) : null}

        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
          <span className="text-xs text-muted-foreground">{refreshHint}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-muted-foreground"
            onClick={() => void cfg.refresh()}
            disabled={cfg.isLoading || !hasBinding}
          >
            <RefreshCw
              aria-hidden="true"
              className={`mr-1 h-3 w-3 ${cfg.isLoading ? 'animate-spin' : ''}`}
            />
            <span>Refresh Schema</span>
          </Button>
        </div>

        {cfg.status.kind === 'fetch_failed' ? (
          <Alert variant="destructive">
            <AlertTriangle aria-hidden="true" className="h-4 w-4" />
            <AlertTitle>CRM Temporarily Unavailable</AlertTitle>
            <AlertDescription>
              {cfg.status.message || 'Could not reach your CRM right now.'}{' '}
              <Button
                variant="link"
                className="h-auto p-0 text-inherit underline"
                onClick={() => void cfg.refresh()}
              >
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        ) : null}

        {cfg.status.kind === 'error' ? (
          <Alert variant="destructive">
            <AlertTriangle aria-hidden="true" className="h-4 w-4" />
            <AlertDescription>{cfg.status.message}</AlertDescription>
          </Alert>
        ) : null}

        <AgentToolCard
          title={`Update ${t.opportunity.toLowerCase()} fields`}
          description={`Let the agent write to allowed custom and system fields on the current CRM ${t.opportunity.toLowerCase()}.`}
          categoryLabel="CRM Application"
          tone="crm_application"
          icon={Edit3}
          checked={isCrmToolEnabled(status, CRM_OPPORTUNITY_TOOL_NAME.updateFields)}
          onCheckedChange={checked => void cfg.toggleUpdateFieldsEnabled(checked)}
          expanded={expandedTools.has(CRM_OPPORTUNITY_TOOL_NAME.updateFields)}
          onExpandedChange={expanded => setToolExpanded(
            setExpandedTools,
            CRM_OPPORTUNITY_TOOL_NAME.updateFields,
            expanded,
          )}
          disabled={!canManage || cfg.savingTool !== null}
          isPending={cfg.savingTool === CRM_OPPORTUNITY_TOOL_NAME.updateFields}
          lockedReason={updateFieldsLockedReason}
        >
          <div className="grid gap-2 sm:grid-cols-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setUpdateFieldsDialogOpen(true)}
              disabled={!canManage || Boolean(updateFieldsLockedReason)}
            >
              <Settings2 aria-hidden="true" className="mr-2 h-4 w-4" />
              <span>Configure Allowed Fields</span>
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onOpenUpdateFieldsInstructions}
              disabled={!canManage}
            >
              <Settings2 aria-hidden="true" className="mr-2 h-4 w-4" />
              <span>Customize Instructions</span>
            </Button>
          </div>
        </AgentToolCard>

        <AgentToolCard
          title={`Move ${t.stage.toLowerCase()}`}
          description={`Let the agent move the current CRM ${t.opportunity.toLowerCase()} to an allowed ${t.pipeline.toLowerCase()} ${t.stage.toLowerCase()}.`}
          categoryLabel="CRM Application"
          tone="crm_application"
          icon={Move}
          checked={isCrmToolEnabled(status, CRM_OPPORTUNITY_TOOL_NAME.moveStage)}
          onCheckedChange={checked => void cfg.toggleMoveStageEnabled(checked)}
          expanded={expandedTools.has(CRM_OPPORTUNITY_TOOL_NAME.moveStage)}
          onExpandedChange={expanded => setToolExpanded(
            setExpandedTools,
            CRM_OPPORTUNITY_TOOL_NAME.moveStage,
            expanded,
          )}
          disabled={!canManage || cfg.savingTool !== null}
          isPending={cfg.savingTool === CRM_OPPORTUNITY_TOOL_NAME.moveStage}
          lockedReason={moveStageLockedReason}
        >
          <div className="grid gap-2 sm:grid-cols-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setMoveStageDialogOpen(true)}
              disabled={!canManage || Boolean(moveStageLockedReason)}
            >
              <Settings2 aria-hidden="true" className="mr-2 h-4 w-4" />
              <span>Configure Allowed Stages</span>
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onOpenMoveStageInstructions}
              disabled={!canManage}
            >
              <Settings2 aria-hidden="true" className="mr-2 h-4 w-4" />
              <span>Customize Instructions</span>
            </Button>
          </div>
        </AgentToolCard>
      </AgentToolCategory>

      <CrmToolConfigDialog
        open={updateFieldsDialogOpen}
        onOpenChange={setUpdateFieldsDialogOpen}
        cfg={cfg}
        toolName={CRM_OPPORTUNITY_TOOL_NAME.updateFields}
        canManage={canManage}
        onOpenInstructions={onOpenUpdateFieldsInstructions}
      />
      <CrmToolConfigDialog
        open={moveStageDialogOpen}
        onOpenChange={setMoveStageDialogOpen}
        cfg={cfg}
        toolName={CRM_OPPORTUNITY_TOOL_NAME.moveStage}
        canManage={canManage}
        onOpenInstructions={onOpenMoveStageInstructions}
      />
    </>
  );
}

function getDeliveryScopeType(channel: CrmToolsChannel): DeliveryScopeType {
  switch (channel) {
    case 'email':
      return 'email_agent';
    case 'messaging':
      return 'message_agent';
    case 'voice':
      return 'voice_agent';
    default: {
      const exhaustiveChannel: never = channel;
      return exhaustiveChannel;
    }
  }
}

function isCrmToolEnabled(
  status: ReturnType<typeof useCrmToolsConfig>['status'],
  toolName: CrmOpportunityToolName,
): boolean {
  return status.kind === 'ok' && status.data.enabledTools.includes(toolName);
}

function setToolExpanded(
  setExpandedTools: Dispatch<SetStateAction<Set<CrmOpportunityToolName>>>,
  toolName: CrmOpportunityToolName,
  expanded: boolean,
): void {
  setExpandedTools(current => {
    const next = new Set(current);
    if (expanded) {
      next.add(toolName);
    } else {
      next.delete(toolName);
    }
    return next;
  });
}

// =====================================================
// Lock info derivation
// =====================================================

function buildToolLockReason(
  status: ReturnType<typeof useCrmToolsConfig>['status'],
  toolName: CrmOpportunityToolName
): string | undefined {
  switch (status.kind) {
    case 'no_binding':
      return "Connect a CRM integration from this agent's CRM tab to configure CRM tools.";
    case 'unsupported_provider':
      return `Your ${status.provider} integration does not expose the APIs required by CRM agent tools.`;
    case 'fetch_failed':
      return 'CRM is temporarily unavailable. Retry before enabling.';
    case 'error':
      return 'CRM schema failed to load.';
    case 'loading':
    case 'idle':
      return 'Loading CRM schema…';
    case 'ok': {
      if (status.data.supportedTools.includes(toolName)) return undefined;
      const providerName =
        status.data.snapshot.provider === 'salesdrive'
          ? 'SalesDrive'
          : status.data.snapshot.provider;
      if (toolName === CRM_OPPORTUNITY_TOOL_NAME.updateFields) {
        return `${providerName} does not support updating ${status.data.terminology.opportunity.toLowerCase()} fields through this tool.`;
      }
      return `${providerName} does not support moving ${status.data.terminology.opportunityPlural.toLowerCase()} between ${status.data.terminology.stagePlural.toLowerCase()}.`;
    }
  }
}
